<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { presentationStore, formattedTime } from '../../stores/presentationStore';
  import { gameStore } from '../../stores/gameStore';

  $: presentation = $presentationStore.currentPresentation;
  $: isPresenter = $presentationStore.isPresenter;
  $: gameState = $gameStore.gameState;

  let timerInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Announce to screen reader
   */
  function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  // Update timer countdown every 100ms
  onMount(() => {
    if (presentation) {
      const message = isPresenter
        ? `You're presenting! Your category is ${presentation.category}. Get ready!`
        : `${presentation.username} is presenting. Category: ${presentation.category}. Get ready to guess!`;
      announceToScreenReader(message, 'assertive');
    }

    timerInterval = setInterval(() => {
      if (gameState?.timerEndTime) {
        const remainingTime = Math.max(0, gameState.timerEndTime - Date.now());
        presentationStore.updateRemainingTime(remainingTime);
      }
    }, 100);
  });

  onDestroy(() => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
  });
</script>

<div class="presenter-intro" role="region" aria-label="Presenter introduction">
  {#if presentation}
    <div class="intro-card">
      <h2>
        {#if isPresenter}
          You're Up!
        {:else}
          {presentation.username} is Presenting
        {/if}
      </h2>

      <div class="category-display">
        <span class="label">Category:</span>
        <span class="category">{presentation.category}</span>
      </div>

      {#if isPresenter}
        <div class="presenter-hint">
          <p>Get ready to give clues for your word!</p>
        </div>
      {:else}
        <div class="guesser-hint">
          <p>Get ready to guess!</p>
        </div>
      {/if}

      <div class="timer" role="timer">
        Starting in {$formattedTime}
      </div>
    </div>
  {/if}
</div>

<style>
  /* Full-screen centered card */
  .presenter-intro {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 2rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .intro-card {
    background: white;
    border-radius: 1rem;
    padding: 3rem;
    max-width: 600px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    text-align: center;
  }

  h2 {
    margin: 0 0 1.5rem 0;
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
  }

  .category-display {
    margin-bottom: 2rem;
    padding: 1rem;
    background: #f3f4f6;
    border-radius: 0.5rem;
  }

  .label {
    font-size: 0.875rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  .category {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-top: 0.25rem;
  }

  .presenter-hint,
  .guesser-hint {
    padding: 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.5rem;
  }

  .presenter-hint {
    background: #fef3c7;
    color: #92400e;
  }

  .guesser-hint {
    background: #dbeafe;
    color: #1e40af;
  }

  .timer {
    font-size: 1.5rem;
    font-weight: 700;
    color: #7c3aed;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .intro-card {
      padding: 2rem;
    }

    h2 {
      font-size: 2rem;
    }

    .category {
      font-size: 1.25rem;
    }

    .opener-text {
      font-size: 1.125rem;
    }
  }
</style>
